import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLastMealDate } from '@/hooks/queries';

export type DateRangePreset = '7d' | '30d' | '90d' | 'month' | 'custom';

export type DateRange = {
  from: Date;
  to: Date;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const formatISODate = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const parseISODate = (s: string | null | undefined): Date | null => {
  if (!s || !ISO_DATE.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const today = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (d: Date, days: number): Date => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);

const presetRange = (preset: DateRangePreset): DateRange => {
  const to = today();
  switch (preset) {
    case '7d':
      return { from: addDays(to, -6), to };
    case '30d':
      return { from: addDays(to, -29), to };
    case '90d':
      return { from: addDays(to, -89), to };
    case 'month':
      return { from: startOfMonth(to), to };
    default:
      return { from: addDays(to, -29), to };
  }
};

/**
 * Read-only resolution of the ISO range from URL params ALONE — for prefetching
 * without instantiating the full `useDateRange` hook (which writes params to the
 * URL on mount). Returns null when the URL carries no range, in which case the
 * caller should defer to the tab's own `useDateRange` (localStorage / last-meal
 * smart default) rather than guess.
 */
export const isoRangeFromSearchParams = (
  params: URLSearchParams,
): { from: string; to: string } | null => {
  const from = parseISODate(params.get('from'));
  const to = parseISODate(params.get('to'));
  if (from && to) return { from: formatISODate(from), to: formatISODate(to) };
  const preset = params.get('preset') as DateRangePreset | null;
  if (preset) {
    const r = presetRange(preset);
    return { from: formatISODate(r.from), to: formatISODate(r.to) };
  }
  return null;
};

export type UseDateRangeReturn = {
  range: DateRange;
  preset: DateRangePreset;
  setRange: (next: DateRange) => void;
  setPreset: (next: DateRangePreset) => void;
  /** ISO `YYYY-MM-DD` strings — what backend endpoints typically expect. */
  iso: { from: string; to: string };
};

const DEFAULT_PRESET: DateRangePreset = '30d';

/**
 * localStorage key prefix for the last range a clinician picked, scoped
 * **per-patient**. Previously this was a single global key, which meant
 * a range chosen for patient A bled over to patient B's first visit and
 * the per-patient smart default (snap-to-last-meal) never fired for
 * subsequent patients. Keying by patient id keeps each patient's window
 * independent while still letting Summary → Logs → Reports carry the
 * range forward within one patient.
 *
 * URL params still win when present — they're shareable + bookmarkable.
 */
// Bumped to .v3 because we changed the serialized shape (added
// `writtenAt`) — `.v2` entries are ignored so the new expiry behavior
// applies immediately. Old keys eventually fall out of localStorage as
// the user re-picks.
const STORAGE_KEY_PREFIX = 'patient.dateRange.v3';
const storageKeyFor = (patientId: number | undefined | null): string =>
  patientId ? `${STORAGE_KEY_PREFIX}.${patientId}` : `${STORAGE_KEY_PREFIX}.global`;

/**
 * Persisted values expire after this window. Long enough for a clinician
 * to step away for lunch and resume on the same patient with the same
 * range; short enough that "yesterday's pick" doesn't outrank fresh
 * smart-default behavior the next morning. Without expiry, a CGM-device
 * range picked once could lock the patient view to those old dates
 * forever, even after new meals are logged.
 */
const PERSISTED_TTL_MS = 4 * 60 * 60 * 1000;

type Persisted = {
  from?: string;
  to?: string;
  preset?: DateRangePreset;
  /** Epoch ms when this value was written; used for TTL expiry on read. */
  writtenAt?: number;
};

const readPersisted = (patientId: number | undefined | null): Persisted | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKeyFor(patientId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const p = parsed as Persisted;
    // Treat values without a timestamp as expired (legacy entries from
    // before this version). Same for entries older than the TTL.
    if (typeof p.writtenAt !== 'number') return null;
    if (Date.now() - p.writtenAt > PERSISTED_TTL_MS) {
      // Don't leave stale data sitting around — clear it so the next
      // visit doesn't pay the read+expire round-trip every time.
      try {
        window.localStorage.removeItem(storageKeyFor(patientId));
      } catch {
        // ignore
      }
      return null;
    }
    return p;
  } catch {
    // ignore
  }
  return null;
};

const writePersisted = (patientId: number | undefined | null, p: Persisted) => {
  if (typeof window === 'undefined') return;
  try {
    const stamped: Persisted = { ...p, writtenAt: Date.now() };
    window.localStorage.setItem(storageKeyFor(patientId), JSON.stringify(stamped));
  } catch {
    // private mode / quota — fall through.
  }
};

/**
 * Date range with URL persistence (`?from=YYYY-MM-DD&to=YYYY-MM-DD&preset=…`)
 * + localStorage fallback + smart per-patient default. Order of precedence
 * on mount:
 *
 *   1. URL params (shareable / bookmarkable, win when present).
 *   2. localStorage (last range the clinician used in this browser session).
 *   3. **Smart default** — when `patientId` is supplied, snap to a 30-day
 *      window ending on the patient's `last_meal_date`. Mirrors v1's
 *      "automatically go to the last range of dates that had meals."
 *   4. Built-in default preset (`30d`).
 *
 * `patientId` is optional so the hook still works on non-patient routes.
 * The smart default runs once after the patient query resolves; subsequent
 * range changes are persisted normally.
 */
export const useDateRange = (patientId?: number): UseDateRangeReturn => {
  const [params, setParams] = useSearchParams();
  // Smart-default source. `useLastMealDate` reads from the
  // `meals_since_last_report` endpoint because `GET /patients/{id}` doesn't
  // return `last_meal_date` (swagger:14223 vs 18746). The earlier
  // implementation read `patient.data.last_meal_date` directly, which was
  // always undefined → smart default silently fell through to the 30d
  // preset and the calendar never snapped to the last meal.
  const lastMeal = useLastMealDate(patientId ?? null);

  // Hydration + smart-default need to RE-FIRE when the user navigates from
  // patient A to patient B. We track which patientId each one last handled;
  // when that doesn't match the current patientId, the effect runs again.
  // A boolean ref was the original implementation — it locked the smart
  // default to "fires once ever," so switching patients carried forward
  // patient A's range instead of snapping to B's last meal.
  const hydratedForRef = useRef<number | null | undefined>(undefined);
  const smartDefaultForRef = useRef<number | null | undefined>(undefined);

  // Hydration: localStorage → URL when the URL has no range params yet.
  // Per-patient keyed so the carried-forward range is scoped to the same
  // patient (no bleed-through between patients).
  useEffect(() => {
    if (hydratedForRef.current === patientId) return;
    hydratedForRef.current = patientId;
    const hasUrlRange = params.get('from') || params.get('to') || params.get('preset');
    if (hasUrlRange) return;
    const persisted = readPersisted(patientId);
    if (!persisted) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (persisted.from) next.set('from', persisted.from);
        if (persisted.to) next.set('to', persisted.to);
        if (persisted.preset) next.set('preset', persisted.preset);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Smart per-patient default: when no URL params AND no per-patient
  // localStorage value, snap to a 30-day window ending on the patient's
  // last logged meal. Mirrors v1's behavior of jumping directly to the
  // most recent data window when a clinician opens a patient.
  useEffect(() => {
    if (smartDefaultForRef.current === patientId) return;
    if (!patientId) return;
    if (lastMeal.isLoading) return;
    const hasUrlRange = params.get('from') || params.get('to') || params.get('preset');
    if (hasUrlRange) return;
    if (readPersisted(patientId)) return;
    const lastMealDate = parseISODate(lastMeal.date);
    if (!lastMealDate) return;
    smartDefaultForRef.current = patientId;
    const fromIso = formatISODate(addDays(lastMealDate, -29));
    const toIso = formatISODate(lastMealDate);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('from', fromIso);
        next.set('to', toIso);
        next.set('preset', 'custom');
        return next;
      },
      { replace: true },
    );
    writePersisted(patientId, { from: fromIso, to: toIso, preset: 'custom' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, lastMeal.date, lastMeal.isLoading]);

  const fromParam = parseISODate(params.get('from'));
  const toParam = parseISODate(params.get('to'));
  const presetParam = (params.get('preset') as DateRangePreset | null) ?? null;

  const preset: DateRangePreset = useMemo(() => {
    if (presetParam && ['7d', '30d', '90d', 'month', 'custom'].includes(presetParam)) {
      return presetParam;
    }
    if (fromParam && toParam) return 'custom';
    return DEFAULT_PRESET;
  }, [presetParam, fromParam, toParam]);

  const range: DateRange = useMemo(() => {
    if (preset !== 'custom') return presetRange(preset);
    return { from: fromParam ?? presetRange(DEFAULT_PRESET).from, to: toParam ?? presetRange(DEFAULT_PRESET).to };
  }, [preset, fromParam, toParam]);

  const updateParams = useCallback(
    (updater: (next: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          updater(next);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setRange = useCallback(
    (next: DateRange) => {
      const fromIso = formatISODate(next.from);
      const toIso = formatISODate(next.to);
      updateParams((p) => {
        p.set('from', fromIso);
        p.set('to', toIso);
        p.set('preset', 'custom');
      });
      writePersisted(patientId, { from: fromIso, to: toIso, preset: 'custom' });
    },
    [updateParams, patientId],
  );

  const setPreset = useCallback(
    (next: DateRangePreset) => {
      updateParams((p) => {
        p.set('preset', next);
        if (next === 'custom') return;
        p.delete('from');
        p.delete('to');
      });
      writePersisted(patientId, { preset: next });
    },
    [updateParams, patientId],
  );

  const iso = useMemo(
    () => ({ from: formatISODate(range.from), to: formatISODate(range.to) }),
    [range],
  );

  return { range, preset, setRange, setPreset, iso };
};

export const __test = { formatISODate, parseISODate, presetRange };
